import { ApiError } from '../../types'
import type {
  ColumnValues,
  FilterClause,
  MarketPage,
  MarketRecord,
  MarketSurvey,
  PossibleDuplicate,
  ShownColumns,
  SurveyColumn,
  SurveyFilter,
  UploadJob,
} from '../../types'

/**
 * The market data half of the browser stand-in.
 *
 * **It has to behave exactly as the deployed system does, not more kindly and
 * not more strictly.** A stand-in that accepts something the product refuses
 * lets a fault through every test and look green; one that refuses something
 * the product allows sends somebody chasing a bug that is not there. Both have
 * happened in this codebase before, which is why the rules below are copied
 * from the server deliberately rather than approximated.
 *
 * The rules mirrored here, each with the file that owns it:
 *   - search matches every word against every value        market-search.ts
 *   - a page never reports "none" while more can be read    market-search.ts
 *   - no total match count is ever returned                 market-search.ts
 *   - filters belong to one survey and never merge          market.ts
 *   - a duplicate warns and never blocks                    market.ts
 *   - thin rows load unless she says otherwise              market-store.ts
 *   - a survey empties in the background, rows before it    market-store.ts
 */

export interface MarketDb {
  surveys: MarketSurvey[]
  records: MarketRecord[]
}

export const freshMarket = (): MarketDb => ({ surveys: [], records: [] })

const normalise = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const searchTextOf = (values: Record<string, string>) => normalise(Object.values(values).join(' '))

const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** Reserved names. Everything else on the query is a column filter. */
export const RESERVED = new Set(['q', 'survey', 'cursor', 'limit'])

export function listMarketData(
  db: MarketDb,
  params: {
    q?: string
    survey?: string
    clauses?: FilterClause[]
    cursor?: string | null
    limit?: number
    from?: number
  },
  /**
   * How many rows one call may read, as the server's time budget does.
   *
   * Unset, every call reads as far as it needs to and a page is always full.
   * Set, a call stops after reading this many rows and answers with what it
   * found and a cursor at where it stopped, which is what the deployed search
   * does on a busy or a large table. Only the stand-in's slow mode sets it.
   */
  reads?: number,
): MarketPage {
  const limit = Math.min(params.limit ?? 100, 200)
  const clauses = (params.clauses ?? []).filter(
    (c) =>
      c.keys.length > 0 &&
      ((c.any?.length ?? 0) > 0 || (c.none?.length ?? 0) > 0 || (c.contains ?? '').trim() !== ''),
  )
  if (params.survey && !db.surveys.some((s) => s.id === params.survey)) {
    throw new ApiError(404, 'That survey no longer exists.')
  }

  const words = normalise(params.q ?? '').split(' ').filter(Boolean)

  let pool = db.records
  if (params.survey) pool = pool.filter((r) => r.survey === params.survey)

  const passes = (r: MarketRecord) => {
    const text = searchTextOf(r.values)
    if (!words.every((w) => text.includes(w))) return false
    /*
      A column is narrowed by ticking several of its values, so a row passes
      when its value is any one of them, and by any of its keys, because the
      same heading has a different key in each survey it came from.

      Typed text is matched ignoring case, the way the server matches it: a
      column holds the provider's own text and nobody types "Engineer" with
      the capital.
    */
    return clauses.every((c) => {
      if ((c.any?.length ?? 0) > 0) {
        if (!c.keys.some((key) => c.any!.includes(r.values[key] ?? ''))) return false
      }
      if ((c.none?.length ?? 0) > 0) {
        // A key the row does not have cannot be holding a barred value, so a
        // row belonging to the other survey is not caught by this.
        if (c.keys.some((key) => key in r.values && c.none!.includes(r.values[key]))) return false
      }
      const text = (c.contains ?? '').trim().toLowerCase()
      if (text) {
        if (!c.keys.some((key) => (r.values[key] ?? '').toLowerCase().includes(text))) return false
      }
      return true
    })
  }

  // The cursor is a position among the matches, mirroring the real one, which
  // is the key of the last row actually returned rather than wherever reading
  // stopped. Both give the same answer: the next page starts at the row after
  // the last one handed back, so no match is skipped.
  /*
    A page can also be asked for directly, by the line it starts at, which is
    how a page number is reached without reading every page in front of it.
    Only inside one survey and only with nothing narrowing it, the same rule
    the server applies.
  */
  const seekable = Boolean(params.survey) && !(params.q ?? '').trim() && clauses.length === 0
  const asked = seekable && params.from ? Math.max(0, params.from - 1) : 0

  if (reads) {
    // The cursor is a place among the rows read rather than among the
    // matches, because a call that stops early has not seen every match.
    const start = Number(params.cursor ?? 0) || asked
    const items: MarketRecord[] = []
    let at = start
    for (; at < pool.length && at < start + reads; at++) {
      if (!passes(pool[at])) continue
      // Read and set aside: the next page starts at this row, not past it.
      if (items.length === limit) break
      items.push(pool[at])
    }
    const cursor = at < pool.length ? String(at) : null
    return { items, cursor, shown: items.length, held: db.records.length, more: cursor !== null }
  }

  const matched = pool.filter(passes)
  const from = Number(params.cursor ?? 0) || asked
  const page = matched.slice(from, from + limit)
  const next = from + limit

  return {
    items: page,
    cursor: next < matched.length ? String(next) : null,
    shown: page.length,
    // What the company holds, not how many matched. The real API cannot know
    // the second without reading every row, so it does not return it and
    // nothing here pretends otherwise.
    held: db.records.length,
    more: next < matched.length,
  }
}

export function surveyFilters(db: MarketDb, surveyId: string): SurveyFilter[] {
  const survey = db.surveys.find((s) => s.id === surveyId)
  if (!survey) throw new ApiError(404, 'That survey no longer exists.')
  return survey.filters ?? []
}

/**
 * One column's values, the way the server serves them.
 *
 * Worked out from the rows here rather than stored, because the stand-in holds
 * a few dozen rows and the server holds a hundred thousand. What matters is
 * that both answer the same question with the same shape.
 */
export function columnValues(db: MarketDb, surveyId: string, columnKey: string): ColumnValues {
  const survey = db.surveys.find((s) => s.id === surveyId)
  if (!survey) throw new ApiError(404, 'That survey no longer exists.')
  const column = survey.columns.find((c) => c.key === columnKey)
  if (!column) throw new ApiError(404, 'That survey has no such column.')

  const set = new Set<string>()
  for (const r of db.records) {
    if (r.survey !== surveyId) continue
    const value = r.values[columnKey]
    if (value === undefined || value.trim() === '') continue
    set.add(value)
    if (set.size > LIST_CAP) {
      return { key: columnKey, heading: column.heading, values: [], tooMany: true }
    }
  }
  return {
    key: columnKey,
    heading: column.heading,
    values: [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    tooMany: false,
  }
}

/** Past this a column has no list and is narrowed by typing. Matches the server. */
const LIST_CAP = 1_000

/**
 * Surveys this file might repeat. A warning and nothing more.
 *
 * Provider and year both matching, or the file name matching. Provider is
 * compared trimmed and case-insensitively, so "Radford" and "radford " are one
 * provider. Nothing is refused on the strength of this.
 */
export function duplicatesAmong(
  surveys: MarketSurvey[],
  provider: string,
  year: string,
  fileName: string,
): PossibleDuplicate[] {
  const out: PossibleDuplicate[] = []
  const wantProviderYear = Boolean(provider.trim() && year.trim())

  for (const survey of surveys) {
    const byProviderYear =
      wantProviderYear && sameText(survey.provider, provider) && sameText(survey.year, year)
    const named = (survey.files ?? []).find((f) => sameText(f.fileName, fileName))
    if (!byProviderYear && !named) continue
    out.push({
      id: survey.id,
      provider: survey.provider,
      year: survey.year,
      fileName: named?.fileName ?? survey.files?.[0]?.fileName ?? '',
      rowCount: survey.rowCount,
      uploadedAt: named?.addedAt ?? survey.uploadedAt,
      matchedOn: byProviderYear ? 'provider and year' : 'file name',
    })
  }
  return out
}

/**
 * Reads a survey file the way the parse Lambda does.
 *
 * The model is not available in the browser, so column names are derived from
 * the headings themselves. That is the one honest difference from the deployed
 * system and it is confined to the naming: **everything that decides what gets
 * stored is the same**, including that the file's own heading row decides how
 * many columns there are.
 */
export function readSurveyFile(text: string): {
  columns: SurveyColumn[]
  rows: { line: number; values: Record<string, string>; filled: number }[]
  samples: Record<string, string>
  unreadable: { count: number; shown: { line: number; reason: string }[] }
  thin: { count: number; shown: number[] }
  filters: SurveyFilter[]
} {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '')
  if (lines.length < 2) {
    throw new ApiError(400, 'That file has a heading row but no rows under it.')
  }

  const headings = splitLine(lines[0]).map((h) => h.trim())
  const columns: SurveyColumn[] = headings.map((heading, i) => ({
    key: keyFor(heading, i),
    heading,
    name: plainName(heading) || heading || `Column ${i + 1}`,
    /*
     * A count is read before a pay figure, because a count column names the
     * measure it belongs to: "Base Salary: Employee Count" holds a number
     * of people and says "salary" while doing it. Tested the other way
     * round it came back as money, the measure then had no counts, and
     * incumbents and organisations were missing from the table.
     *
     * Word boundaries matter in both. Without them "Country" matches
     * "count" and a column of country names is offered to her as a number.
     */
    kind: /\b(count|incumbents?|companies|organi[sz]ations|participants|observations)\b|\bnumber of\b/i.test(heading)
      ? 'number'
      : /\b(sal|salary|pay|comp|cash|bonus|pctl|percentile|median)\b|\bp\d{2}\b/i.test(heading)
        ? 'money'
        : /\b(number|n)\b/i.test(heading)
          ? 'number'
          : 'text',
    // Only where the heading itself names one. Never guessed from a value.
    ...(headings[i].match(/\b(USD|EUR|GBP|INR|SGD|AUD|CAD|JPY|CHF)\b/i)
      ? { currency: headings[i].match(/\b(USD|EUR|GBP|INR|SGD|AUD|CAD|JPY|CHF)\b/i)![1].toUpperCase() }
      : {}),
  }))

  const rows: { line: number; values: Record<string, string>; filled: number }[] = []
  const shown: { line: number; reason: string }[] = []
  const samples: Record<string, string> = {}
  let unreadableCount = 0

  lines.slice(1).forEach((raw, i) => {
    const line = i + 1
    const cells = splitLine(raw)
    if (cells.length !== columns.length) {
      unreadableCount++
      if (shown.length < 50) {
        shown.push({
          line,
          reason: `has ${cells.length} cells where the file's heading row has ${columns.length}`,
        })
      }
      return
    }
    const values: Record<string, string> = {}
    let filled = 0
    columns.forEach((col, c) => {
      const value = cells[c] ?? ''
      values[col.key] = value
      if (value.trim() !== '') {
        filled++
        if (samples[col.key] === undefined) samples[col.key] = value
      }
    })
    rows.push({ line, values, filled })
  })

  return {
    columns,
    rows,
    samples,
    unreadable: { count: unreadableCount, shown },
    thin: thinRows(rows),
    filters: filtersFrom(columns, rows),
  }
}

/** Mirrors the server: counted against the rest of the file, never judged. */
function thinRows(
  rows: { line: number; filled: number }[],
): { count: number; shown: number[] } {
  if (rows.length < 4) return { count: 0, shown: [] }
  const counts = rows.map((r) => r.filled).sort((a, b) => a - b)
  const typical = counts[Math.floor(counts.length / 2)]
  const threshold = Math.floor(typical / 2)
  if (threshold < 1) return { count: 0, shown: [] }
  const thin = rows.filter((r) => r.filled <= threshold)
  return { count: thin.length, shown: thin.slice(0, 50).map((r) => r.line) }
}

/**
 * Mirrors `FilterCollector` in backend/src/lib/market-rows.ts. Change both.
 *
 * **It used to drop a column holding a single value**, on the reasoning that
 * a dropdown offering one choice is no choice. The server never did that: it
 * keeps the column and its one value, and a column over the cap is reported
 * with `tooMany` rather than left out.
 *
 * The difference was invisible until the table started folding constant
 * columns out of itself and saying them once above it, which reads that
 * single value. Against the mock no column ever folded, so the screen looked
 * unchanged and the rule looked broken.
 */
function filtersFrom(
  columns: SurveyColumn[],
  rows: { values: Record<string, string> }[],
): SurveyFilter[] {
  const out: SurveyFilter[] = []
  for (const col of columns) {
    const seen = new Set<string>()
    let tooMany = false
    for (const row of rows) {
      const value = row.values[col.key]
      if (!value || value.trim() === '') continue
      seen.add(value)
      // The server's cap, not a smaller one of our own.
      if (seen.size > 1000) {
        tooMany = true
        break
      }
    }
    // Reported, not dropped: the screen has to know the column exists and
    // which kind of filter it takes.
    if (tooMany) {
      out.push({ key: col.key, heading: col.heading, values: [], tooMany: true })
      continue
    }
    // A column nothing filled has nothing to list and nothing to type
    // against either, so it is left out entirely.
    if (seen.size === 0) continue
    out.push({
      key: col.key,
      heading: col.heading,
      values: [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    })
  }
  return out
}

/**
 * A plain-words name for a heading.
 *
 * The real system asks the model, which reads an abbreviation properly. This
 * expands the handful that appear in almost every provider file and otherwise
 * hands the heading back unchanged. It is the one honest difference between
 * this stand-in and the deployed system, and it is confined to the wording she
 * sees before confirming: nothing about what gets stored depends on it.
 */
function plainName(heading: string): string {
  let out = heading.trim()
  if (!out) return ''
  const swaps: [RegExp, string][] = [
    [/\bSal\b/gi, 'salary'],
    [/\bAnn\b/gi, 'annual'],
    [/\bPctl\b/gi, 'percentile'],
    [/\bTot\b/gi, 'total'],
    [/\bComp\b/gi, 'compensation'],
    [/\bIncl\b/gi, 'including'],
    /*
     * A comma before the percentile, because that is the shape the model
     * produces and the shape the product reads.
     *
     * Everything downstream splits a confirmed name on its first comma: what
     * is before it is the measure ("Base salary", "Total cash actual") and
     * what is after it says which figure of that measure this column holds.
     * Without the comma a file has no measures at all, so the benchmarking
     * index skips it and the table draws no pay figures.
     *
     * This stand-in wrote "Base salary 50th percentile USD" with no comma,
     * and nothing noticed until the table started reading percentiles
     * through the same resolver the agent uses.
     */
    [/\bP(\d{2})\b/g, ', $1th percentile'],
    [/\b(\d{2})(?:th|st|nd|rd)\s+percentile\b/gi, ', $1th percentile'],
  ]
  for (const [from, to] of swaps) out = out.replace(from, to)
  // One comma, however many of the swaps above fired.
  out = out.replace(/,\s*,/g, ',').replace(/\s+,/g, ',')
  out = out.charAt(0).toUpperCase() + out.slice(1).toLowerCase()
  // A currency code stays shouted. "usd" reads as a typo.
  return out.replace(/\b(usd|eur|gbp|inr|sgd|aud|cad|jpy|chf)\b/g, (m) => m.toUpperCase())
}

function keyFor(name: string, index: number): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
  return slug ? `${slug}_${index}` : `col_${index}`
}

/** Same splitting the shared CSV reader does, quotes included. */
function splitLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"'
        i++
      } else if (ch === '"') quoted = false
      else cur += ch
    } else if (ch === '"') quoted = true
    else if (ch === ',') {
      out.push(cur)
      cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out
}

/**
 * Writes a survey's rows, the way the background worker does.
 *
 * Thin rows are left out only when asked. A row's id comes from the job and
 * the line, so running this twice writes the same records rather than a second
 * copy of them, exactly as the real import behaves when it resumes.
 */
export function storeSurvey(
  db: MarketDb,
  job: UploadJob,
  detail: NonNullable<UploadJob['market']>,
  rows: { line: number; values: Record<string, string> }[],
  dropThin: boolean,
  uploadedBy: string,
): MarketSurvey {
  const columns = detail.columns ?? []
  const thinLines = new Set(dropThin ? (detail.thin?.shown ?? []) : [])
  const keep = rows.filter((r) => !thinLines.has(r.line))

  let survey = detail.surveyId ? db.surveys.find((s) => s.id === detail.surveyId) : undefined
  let lineOffset = 0

  if (survey) {
    // A heading this survey has never had becomes a new column on it. Records
    // already stored are not touched and simply have no value for it.
    const known = new Set(survey.columns.map((c) => c.heading.trim().toLowerCase()))
    const added = columns.filter((c) => !known.has(c.heading.trim().toLowerCase()))
    if (added.length) survey.columns = [...survey.columns, ...added]
    // Line numbers continue rather than restarting, or two rows would share
    // one and the survey would stop reading in its file's order.
    lineOffset = db.records
      .filter((r) => r.survey === survey!.id)
      .reduce((top, r) => Math.max(top, r.line), 0)
  } else {
    survey = {
      id: `sv_${job.id.replace(/^up_/, '')}`,
      provider: detail.provider,
      year: detail.year,
      industry: detail.industry,
      uploadedBy,
      uploadedAt: new Date().toISOString(),
      rowCount: 0,
      files: [],
      columns,
      filters: detail.filters ?? [],
    }
    db.surveys.unshift(survey)
  }

  const byId = new Map(db.records.map((r) => [r.id, r]))
  for (const row of keep) {
    const id = `mkt_${job.id.replace(/^up_/, '')}_${String(row.line).padStart(7, '0')}`
    const rec: MarketRecord = {
      id,
      survey: survey.id,
      line: lineOffset + row.line,
      sourceFile: job.fileName,
      sourceLine: row.line,
      values: row.values,
    }
    // Same id means the same record, which is what makes a repeat safe.
    if (byId.has(id)) Object.assign(byId.get(id)!, rec)
    else {
      db.records.push(rec)
      byId.set(id, rec)
    }
  }

  survey.rowCount = db.records.filter((r) => r.survey === survey!.id).length
  survey.files = [
    ...(survey.files ?? []),
    {
      fileName: job.fileName,
      addedBy: uploadedBy,
      addedAt: new Date().toISOString(),
      rowCount: keep.length,
    },
  ]
  return survey
}

/**
 * Empties a survey, rows before the survey record.
 *
 * The real one does this in the background over several runs. Here it is
 * immediate, because there is nothing to wait for, but the order is the same:
 * interrupted half way, what is left is a survey with fewer rows that is still
 * visible and still removable.
 */
export function removeSurvey(db: MarketDb, surveyId: string, confirmProvider: string): number {
  const survey = db.surveys.find((s) => s.id === surveyId)
  if (!survey) throw new ApiError(404, 'That survey no longer exists.')

  if (!sameText(confirmProvider, survey.provider)) {
    throw new ApiError(
      400,
      `Type "${survey.provider}" to confirm. This removes ${survey.rowCount} records and ` +
      'cannot be undone.',
    )
  }

  const before = db.records.length
  db.records = db.records.filter((r) => r.survey !== surveyId)
  db.surveys = db.surveys.filter((s) => s.id !== surveyId)
  return before - db.records.length
}

/**
 * Which column holds each field the table shows.
 *
 * **Mirrors `shownColumns` in backend/src/lib/vectors.ts. Change both.**
 * `TC-M-087` runs the two against the same column lists and fails if they
 * disagree, so the copies cannot drift the way the filter list did.
 *
 * Read from the plain English name an admin confirmed, never from the
 * provider's raw heading and never from the stored key. `mjlcode_4` means
 * nothing; "Job code" is what a person agreed it was.
 */
export function shownColumns(columns: SurveyColumn[]): ShownColumns {
  const named = (patterns: RegExp[], kind: SurveyColumn['kind'] = 'text') => {
    for (const pattern of patterns) {
      const hit = columns.find((c) => c.kind === kind && pattern.test(String(c.name ?? '')))
      if (hit) return hit.key
    }
    return undefined
  }

  /*
   * Where a confirmed name stops naming the measure and starts describing the
   * figure: the first comma or colon, whichever comes first. Mercer writes
   * "Base salary, annual, 50th percentile" and Radford writes
   * "Market (5) CFY Base Salary: 10th Percentile".
   */
  const splitAt = (name: string) => {
    const marks = [name.indexOf(','), name.indexOf(':')].filter((i) => i > 0)
    return marks.length ? Math.min(...marks) : -1
  }

  type Measure = {
    name: string; hourly: boolean
    orgs?: string; incumbents?: string
    p25?: string; p50?: string; p75?: string; p90?: string
  }

  const groups = new Map<string, Measure>()
  for (const column of columns) {
    const name = String(column.name ?? '')
    const at = splitAt(name)
    if (at <= 0) continue
    const rest = name.slice(at + 1).toLowerCase()

    /*
     * The fiscal year belongs to the measure, not to the figure. Grouped on
     * the leading words alone, a file carrying both years offered one
     * "Base salary" holding whichever was read first.
     */
    const head = name.slice(0, at).trim()
    /*
     * The year can be written on either side of the split. A confirmed name
     * puts it after, a raw provider heading puts it before. Reading only one
     * side merged both years into a single measure.
     */
    const yearIn = (part: string) => /\bcurrent fiscal year\b|\bcfy\b/i.test(part)
      ? ', current fiscal year'
      : /\blast fiscal year\b|\blfy\b/i.test(part)
        ? ', last fiscal year'
        : ''
    const group = head + (yearIn(head) ? '' : yearIn(rest))
    if (group === '') continue

    const measure = groups.get(group) ?? { name: group, hourly: /hourly|per hour/i.test(group) }

    // Radford writes "employee count" and "company count" where Mercer writes
    // "number of employees" and "number of organisations".
    if (column.kind === 'number' && /number of organi[sz]ations|company count|orgs\b|participants|companies/.test(rest)) {
      measure.orgs ??= column.key
    } else if (column.kind === 'number' && /number of (observations|incumbents|employees)|employee count|incumbent count/.test(rest)) {
      measure.incumbents ??= column.key
    } else if (column.kind === 'money') {
      // Median before 50th, because a file carrying both means the same
      // thing twice and either is right.
      if (/\b25th\b|\bp25\b/.test(rest)) measure.p25 ??= column.key
      else if (/\bmedian\b|\b50th\b|\bp50\b/.test(rest)) measure.p50 ??= column.key
      else if (/\b75th\b|\bp75\b/.test(rest)) measure.p75 ??= column.key
      else if (/\b90th\b|\bp90\b/.test(rest)) measure.p90 ??= column.key
    }
    groups.set(group, measure)
  }
  const measures = [...groups.values()].filter((m) => m.p50 !== undefined)

  const rank = (name: string) =>
    /^base salary\b/i.test(name) ? 100
      : /^annual base\b/i.test(name) ? 95
        : /^total cash actual/i.test(name) ? 80
          : /^total cash target/i.test(name) ? 70
            : /^total (annual )?comp/i.test(name) ? 60
              : /hourly/i.test(name) ? -100 : 0
  /** This year's figures beat last year's. */
  const year = (name: string) =>
    /\bcurrent fiscal year\b|\bcfy\b/i.test(name) ? 2
      : /\blast fiscal year\b|\blfy\b/i.test(name) ? 1 : 0

  const measure = [...measures]
    .sort((a, b) => (rank(b.name) - rank(a.name)) || (year(b.name) - year(a.name)))[0]

  /**
   * The best measure whose name matches one of these, this year's preferred.
   * Patterns in order, so "target total cash" wins over a bare "total cash".
   */
  const measureLike = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const hits = measures.filter((m) => pattern.test(m.name))
      if (hits.length) return [...hits].sort((a, b) => year(b.name) - year(a.name))[0]
    }
    return undefined
  }
  // Not anchored: a raw provider heading leads with its own numbering.
  const cash = measureLike([/\btarget total cash\b/i, /\btotal cash actual\b/i, /\btotal cash\b/i])
  const equity = measureLike([
    /long.?term incentive stock value/i,
    /long.?term incentive value/i,
    /\bequity\b/i,
  ])

  /*
   * A count column standing on its own, where the measure carries none.
   * Whatever kind it was typed as: the confirmed name is what says what it
   * is, and a bare "Organisations" column reads as text often enough.
   */
  const standalone = (patterns: RegExp[]) => {
    for (const pattern of patterns) {
      const hit = columns.find((c) => pattern.test(String(c.name ?? '')))
      if (hit) return hit.key
    }
    return undefined
  }

  return {
    code: named([/\bjob code\b/i, /\bcode\b/i, /\bmjl\b/i]),
    title: named([/\bjob title\b/i, /\bposition title\b/i, /\btitle\b/i, /\bjob name\b/i]),
    family: named([/\bjob family\b/i, /\bfamily\b/i, /\bdiscipline\b/i, /\bfunction\b/i]),
    level: named([/\bjob level\b/i, /\blevel\b/i, /\bgrade\b/i, /\bcareer band\b/i]),
    country: named([/\bcountry\b/i, /\bgeograph/i, /\blocation\b/i, /\bregion\b/i]),
    incumbents: measure?.incumbents
      ?? standalone([/\bincumbents\b/i, /\bobservations\b/i, /\bemployees\b/i]),
    orgs: measure?.orgs
      ?? standalone([/\borgani[sz]ations\b/i, /\borgs\b/i, /\bparticipants\b/i, /\bcompanies\b/i]),
    p25: measure?.p25,
    p50: measure?.p50,
    p75: measure?.p75,
    p90: measure?.p90,
    cash25: cash?.p25,
    cash50: cash?.p50,
    cash75: cash?.p75,
    cashMeasure: cash?.name,
    equity25: equity?.p25,
    equity50: equity?.p50,
    equity75: equity?.p75,
    equityMeasure: equity?.name,
    currency: named([/\bcurrency\b/i]),
    unit: measure?.hourly ? 'hourly' : 'annual',
    measure: measure?.name,
  }
}
